import { useCallback, useEffect, useState } from 'react'
import { api, ErroreApi } from '../../servizi/api.ts'
import { Avviso, Vuoto } from '../../componenti/Pezzi.tsx'
import { Bottone, Campo, Riquadro, Scelta, Tabella } from '../../componenti/base.tsx'

/**
 * Altri codici della persona: STP, ENI, CUI della Questura, numero di una ricevuta (indicazioni
 * CIDIS del 17/10/2026). Il codice fiscale numerico provvisorio e' di 11 cifre per norma e resta
 * nel campo del codice fiscale; qui vanno gli altri numeri, che prima finivano li' e non tornavano
 * con le 11 cifre. Si ritrovano scrivendoli nella ricerca per codice fiscale.
 */

type Codice = { id: string; tipo: string; valore: string | null; rilasciato_da: string | null; scadenza: string | null }

const TIPI = [
  { valore: 'STP', etichetta: 'STP – Straniero temporaneamente presente (sanità)' },
  { valore: 'ENI', etichetta: 'ENI – Europeo non iscritto (sanità)' },
  { valore: 'CUI', etichetta: 'CUI – Codice unico della Questura' },
  { valore: 'ALTRO', etichetta: 'Altro (per esempio il numero di una ricevuta)' },
]
const nomeTipo = (tipo: string) => TIPI.find((t) => t.valore === tipo)?.etichetta.split(' – ')[0] ?? tipo

export function AltriCodici({ personaId, puoModificare, vedeValori }: {
  personaId: string; puoModificare: boolean; vedeValori: boolean
}) {
  const [codici, setCodici] = useState<Codice[] | null>(null)
  const [aggiungi, setAggiungi] = useState(false)
  const [tipo, setTipo] = useState('')
  const [valore, setValore] = useState('')
  const [rilasciatoDa, setRilasciatoDa] = useState('')
  const [scadenza, setScadenza] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState<string | null>(null)

  const carica = useCallback(() => {
    api.get<Codice[]>(`/persone/${personaId}/identificativi`)
      .then(setCodici)
      .catch(() => setCodici([]))
  }, [personaId])
  useEffect(carica, [carica])

  async function salva() {
    setErrore(null); setInCorso('nuovo')
    try {
      await api.post(`/persone/${personaId}/identificativi`, {
        tipo, valore, rilasciato_da: rilasciatoDa.trim() || null, scadenza: scadenza || null,
      })
      setAggiungi(false); setTipo(''); setValore(''); setRilasciatoDa(''); setScadenza('')
      carica()
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine. Riprova fra poco.')
    } finally {
      setInCorso(null)
    }
  }

  async function togli(c: Codice) {
    setErrore(null); setInCorso(c.id)
    try {
      await api.elimina(`/persone/${personaId}/identificativi/${c.id}`)
      carica()
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine. Riprova fra poco.')
    } finally {
      setInCorso(null)
    }
  }

  const scrive = puoModificare && vedeValori
  return (
    <Riquadro titolo="Altri codici" denso azioni={scrive && !aggiungi
      ? <Bottone variante="secondario" dimensione="piccolo" onClick={() => setAggiungi(true)}>Aggiungi un codice</Bottone>
      : undefined}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {aggiungi && (
        <form className="griglia-modulo" onSubmit={(e) => { e.preventDefault(); void salva() }}>
          <Scelta etichetta="Che codice è" obbligatorio vuota="— scegli —" valore={tipo} opzioni={TIPI} cambia={setTipo} />
          <Campo etichetta="Codice" obbligatorio valore={valore} maxLength={60} cambia={setValore} autoComplete="off" />
          <Campo etichetta={tipo === 'ALTRO' ? 'Che cos’è, o chi l’ha rilasciato' : 'Rilasciato da'}
                 obbligatorio={tipo === 'ALTRO'} valore={rilasciatoDa} maxLength={120} cambia={setRilasciatoDa}
                 aiuto={tipo === 'ALTRO' ? 'Per esempio: ricevuta della Questura di Caserta.' : 'Facoltativo.'} />
          <Campo etichetta="Scade il" tipo="date" valore={scadenza} cambia={setScadenza} aiuto="Facoltativo." />
          <div className="riga">
            <Bottone variante="primario" tipo="submit" inCorso={inCorso === 'nuovo'}
                     disabled={!tipo || valore.trim().length < 3 || (tipo === 'ALTRO' && !rilasciatoDa.trim())}>
              Salva il codice
            </Bottone>
            <Bottone variante="discreto" onClick={() => { setAggiungi(false); setErrore(null) }}>Annulla</Bottone>
          </div>
        </form>
      )}
      {codici && (
        <Tabella intestazioni={['Codice', 'Valore', 'Rilasciato da', 'Scade il', ...(scrive ? [''] : [])]}
                 vuoto={<Vuoto testo="Nessun altro codice. STP, CUI e ricevute si aggiungono qui." />}>
          {codici.map((c) => (
            <tr key={c.id}>
              <td data-etichetta="Codice">{nomeTipo(c.tipo)}</td>
              <td data-etichetta="Valore" className="mono">{c.valore ?? <span className="tenue">presente</span>}</td>
              <td data-etichetta="Rilasciato da">{c.rilasciato_da ?? <span className="tenue">—</span>}</td>
              <td data-etichetta="Scade il">{c.scadenza ? new Date(c.scadenza).toLocaleDateString('it-IT') : <span className="tenue">—</span>}</td>
              {scrive && (
                <td>
                  <Bottone variante="discreto" dimensione="piccolo" inCorso={inCorso === c.id} onClick={() => void togli(c)}>
                    Togli
                  </Bottone>
                </td>
              )}
            </tr>
          ))}
        </Tabella>
      )}
    </Riquadro>
  )
}
